<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import PreviousActivityItem from '../components/PreviousActivityItem.vue'
import { useQuasar } from 'quasar'
import { useCurrentCycle } from '../store/useCurrentCycle'
import { useTimesheets } from '../store/timesheets'
import { storeToRefs } from 'pinia'
import { usePayrollWindow } from '../store/usePayrollWindow'
import { useFormatDate } from '../composables/formatDate'
import PreviousActivitiesScorecardsAll from '../components/PreviousActivitiesScorecardsAll.vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '../store/useUserStore'
import { onBeforeRouteLeave } from 'vue-router'
import { isNullish } from '../utils'
//for ripple
const $q = useQuasar()
defineExpose({ $q })

//translations
const { t } = useI18n({ useScope: 'global' })
const label = (key: string) => t(`payPeriod.${key}`)

//timeline items
const { getTimesheets } = storeToRefs(useTimesheets())
const timesheetItems = ref(getTimesheets)

//cycle
const { getCycle } = storeToRefs(useCurrentCycle())
//payroll window text
const payroll = storeToRefs(usePayrollWindow())
const store = storeToRefs(useUserStore())

const payrollWindowText = computed(() => {
  if (payroll.current.value) {
    const start = useFormatDate(payroll.current.value.start, { language_choice: store.language })
    const end = useFormatDate(payroll.current.value.end, { language_choice: store.language })
    return `${start.weekday()} ${start.month()} ${start.day()} - ${end.weekday()} ${end.month()} ${end.day()} `
  } else {
    return ''
  }
})
const globalTimesheetInstance = inject('globalTimesheetInstance')

const hasReadyToSync = computed(() => {
  return timesheetItems.value.some((item) => item.readyToSync)
})
onBeforeRouteLeave(async (to, from, next) => {
  // Exhaustive check that the user is not
  // navigating to a previous activity that is not ready to sync
  // This is usually done with the back button.
  if (hasReadyToSync.value) {
    if (to.name != 'previous_activity') {
      return next()
    }
    if (isNullish(to.params.id)) {
      return next()
    }
    const timesheet = timesheetItems.value[Number(to.params.id)]
    if (timesheet && !timesheet.readyToSync) {
      return next()
    }
    return next(false)
  } else {
    return next()
  }
})
</script>
<template>
  <div class="q-pa-md">
    <h1 class="text-h4 text-weight-medium">{{ label('title') }}</h1>

    <div class="row q-gutter-lg">
      <div>
        <p>{{ payrollWindowText }}</p>
        <p>
          {{ label('cycle') }} {{ getCycle && getCycle.cycle_number ? getCycle.cycle_number : '' }}
        </p>
      </div>

      <div class="col">
        <div class="row q-gutter-sm">
          <PreviousActivitiesScorecardsAll :timesheets="timesheetItems" />
        </div>
      </div>
    </div>
    <div class="row q-py-md">
      <div class="col">
        <p>{{ label('delayNote') }}</p>
      </div>
    </div>
  </div>

  <q-list>
    <template v-for="(item, index) in timesheetItems" :key="item">
      <PreviousActivityItem
        :to="
          globalTimesheetInstance || item.readyToSync ? undefined : `/previous_activities/${index}`
        "
        :fix-by="payroll.current.value ? payroll.current.value.end : undefined"
        v-bind="item"
      />
      <q-separator v-if="index < timesheetItems.length - 1" spaced inset />
    </template>
  </q-list>
</template>
